import { useMemo } from "react";
import Chart from "react-apexcharts";
import styles from "../../../../styles/NavigationLogs.module.css";
import { GetNavigationLogsDto } from "./types";

interface LogsChartsProps {
  logs: GetNavigationLogsDto[];
}

const TYPE_COLORS: Record<string, string> = {
  NAVIGATION: "#378ADD",
  CLICK: "#639922",
  API: "#BA7517",
  ERROR: "#E24B4A",
};

export const LogsCharts = ({ logs }: LogsChartsProps) => {
  const { typeLabels, typeCounts, typeColors } = useMemo(() => {
    const map: Record<string, number> = {};
    logs.forEach((l) => { map[l.type] = (map[l.type] ?? 0) + 1; });
    const labels = Object.keys(map);
    return {
      typeLabels: labels,
      typeCounts: labels.map((k) => map[k]),
      typeColors: labels.map((k) => TYPE_COLORS[k] ?? "#888780"),
    };
  }, [logs]);

  const { pageLabels, pageAvgs } = useMemo(() => {
    const map: Record<string, number[]> = {};
    logs
      .filter((l) => l.duration != null)
      .forEach((l) => {
        if (!map[l.page]) map[l.page] = [];
        map[l.page].push(l.duration!);
      });
    const pages = Object.keys(map).slice(0, 6);
    return {
      pageLabels: pages,
      pageAvgs: pages.map((p) =>
        Math.round(map[p].reduce((a, b) => a + b, 0) / map[p].length)
      ),
    };
  }, [logs]);

  const baseOptions: ApexCharts.ApexOptions = {
    chart: { toolbar: { show: false }, fontFamily: "inherit", animations: { enabled: true, speed: 350 } },
    grid: { borderColor: "rgba(0,0,0,0.06)", strokeDashArray: 4, xaxis: { lines: { show: false } } },
    tooltip: { theme: "light" },
    dataLabels: { enabled: false },
  };

  return (
    <div className={styles.chartsRow}>
      <div className={styles.chartBox}>
        <div className={styles.chartTitle}>Logs por tipo</div>
        <Chart
          type="bar"
          height={170}
          series={[{ name: "Logs", data: typeCounts }]}
          options={{
            ...baseOptions,
            colors: typeColors,
            plotOptions: { bar: { borderRadius: 4, distributed: true, columnWidth: "55%" } },
            xaxis: { categories: typeLabels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "11px", colors: "#888780" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#888780" } } },
            legend: { show: false },
          }}
        />
      </div>

      <div className={styles.chartBox}>
        <div className={styles.chartTitle}>Duração média por página (ms)</div>
        <Chart
          type="bar"
          height={170}
          series={[{ name: "Avg ms", data: pageAvgs }]}
          options={{
            ...baseOptions,
            colors: ["#B5D4F4"],
            plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "55%" } },
            xaxis: { categories: pageLabels, axisBorder: { show: false }, axisTicks: { show: false }, labels: { style: { fontSize: "10px", colors: "#888780" } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#888780" }, maxWidth: 120 } },
          }}
        />
      </div>
    </div>
  );
};
